// navbar.component.ts
import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, Output, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { SubNavItem } from '@foxera/core/interfaces/sideNav.interface';
import { ApplicationRoutes } from '@foxera/shared/constants/application.routes';
import { PermissionService } from '@foxera/shared/services/permission.service';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css'],
  imports: [RouterLink, CommonModule],
})
export class NavBarComponent {
  @Output() subNavChange = new EventEmitter<SubNavItem[]>();
  @Output() activeSubNavChange = new EventEmitter<SubNavItem | null>();

  constructor(
    private router: Router,
    private permissionService: PermissionService
  ) { }

  private readonly allNavItems: {
    label: string;
    link: string;
    icon: string;
    subNav?: SubNavItem[];
  }[] = [
      // {
      //   label: 'Dashboard',
      //   link: `/${ApplicationRoutes.DASHBOARD}`,
      //   icon: 'icon-dashboard-nav'
      // },
      {
        label: 'Schedule',
        link: `/${ApplicationRoutes.SCHEDULE}`,
        icon: 'icon-schedule-nav'
      },
      {
        label: 'Clients',
        link: `/${ApplicationRoutes.CLIENTS}`,
        icon: 'icon-clients-nav',
      },
      {
        label: 'Active Plans',
        link: `/${ApplicationRoutes.ACTIVE_PLANS}`,
        icon: 'icon-biceps-flexed',
      },
      {
        label: 'Statistics',
        link: `/${ApplicationRoutes.STATISTICS}`,
        icon: 'icon-chart-line',
        subNav: [
          { label: 'Payments', link: `/${ApplicationRoutes.STATISTICS_PAYMENTS}` },
          { label: 'Invoices', link: `/${ApplicationRoutes.STATISTICS_INVOICES}` },
        ],
      },

      {
        label: 'Catalog',
        link: `/${ApplicationRoutes.CATALOG}`,
        icon: 'icon-services-nav',
        subNav: [
          { label: 'Services', link: `/${ApplicationRoutes.CATALOG_SERVICES}` },
          { label: 'Memberships', link: `/${ApplicationRoutes.CATALOG_MEMBERSHIPS}` },
        ],
      },
      {
        label: 'Employees',
        link: `/${ApplicationRoutes.EMPLOYEE}`,
        icon: 'icon-employees-nav',
        subNav: [
          { label: 'Team Members', link: `/${ApplicationRoutes.EMPLOYEE_TEAM_MEMBERS}` },
          { label: 'Schedule Shifts', link: `/${ApplicationRoutes.EMPLOYEE_SCHEDULE_SHIFTS}` },
        ],
      },
      {
        label: 'Settings',
        link: `/${ApplicationRoutes.SETTINGS}`,
        icon: 'icon-settings-nav',
        subNav: [],
      },
    ];

  // Map of nav item labels to their required permission checks
  private readonly navItemPermissionMap: Record<string, () => boolean> = {
    'Active Plans': () => this.permissionService.canViewClientWorkoutPlans(),
    'Statistics': () =>
      this.permissionService.canAccessInvoices() ||
      this.permissionService.canAccessPayments(),
    'Catalog': () => this.permissionService.canAccessCatalog(),
    'Employees': () => this.permissionService.canAccessEmployees(),
    'Settings': () => this.permissionService.canAccessSettings()
  };

  // Map of subNav item labels to their required permission checks
  private readonly subNavItemPermissionMap: Record<string, () => boolean> = {
    'Invoices': () => this.permissionService.canAccessInvoices(),
    'Payments': () => this.permissionService.canAccessPayments()
  };

  navItems = computed(() => {
    return this.allNavItems
      .filter(item => {
        const permissionCheck = this.navItemPermissionMap[item.label];
        // If there's no permission requirement, show the item
        if (!permissionCheck) {
          return true;
        }
        // If there's a permission requirement, check if user has it
        return permissionCheck();
      })
      .map(item => {
        // Filter subNav items based on permissions
        if (item.subNav && item.subNav.length > 0) {
          return {
            ...item,
            subNav: item.subNav.filter(subItem => {
              const subNavPermissionCheck = this.subNavItemPermissionMap[subItem.label];
              // If there's no permission requirement for this subNav item, show it
              if (!subNavPermissionCheck) {
                return true;
              }
              // If there's a permission requirement, check if user has it
              return subNavPermissionCheck();
            })
          };
        }
        return item;
      });
  });

  // Get a subset of nav items for mobile (max 4-5 items)
  mobileNavItems = computed(() => {
    const items = this.navItems();
    // Show first 3 items for mobile - Schedule, Clients, and Statistics
    const mobileItems = [
      items.find(item => item.label === 'Schedule'),
      items.find(item => item.label === 'Clients'),
      items.find(item => item.label === 'Statistics'),
    ].filter((item): item is NonNullable<typeof item> => item !== undefined);
    
    return mobileItems.slice(0, 3);
  });

  activeItem = signal<{
    label: string;
    link: string;
    icon: string;
    subNav?: SubNavItem[];
  } | null>(null);
  
  indicatorPosition: string = '0px';
  indicatorWidth: string = '0px';

  // Computed signals to check active item position
  isActiveItemOnEdge = computed(() => {
    const active = this.activeItem();
    if (!active) return false;
    const filteredItems = this.navItems();
    const activeIndex = filteredItems.findIndex(
      (item) => item === active
    );
    return activeIndex === 0 || activeIndex === filteredItems.length - 1;
  });

  isActiveItemFirst = computed(() => {
    const active = this.activeItem();
    const filteredItems = this.navItems();
    return active ? filteredItems[0] === active : false;
  });

  isActiveItemLast = computed(() => {
    const active = this.activeItem();
    const filteredItems = this.navItems();
    return active
      ? filteredItems[filteredItems.length - 1] === active
      : false;
  });

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setIndicatorPosition();
    }, 100);
  }

  ngOnInit(): void {
    // Set initial active item based on current route
    this.setActiveItemFromRoute(this.router.url);

    // Listen for route changes
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.setActiveItemFromRoute(event.url);
      });

    // Listen for window resize (which triggers media queries and font changes)
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  ngOnDestroy(): void {
    // Clean up event listener
    window.removeEventListener('resize', this.handleResize.bind(this));
  }

  private handleResize(): void {
    // Debounce to avoid excessive calculations
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.setIndicatorPosition();
    }, 200);
  }

  private resizeTimeout: any;

  setActiveItemFromRoute(url: string): void {
    // Find the matching nav item based on the current route
    const filteredItems = this.navItems();
    let matchedItem = null;

    for (const item of filteredItems) {
      if (url.startsWith(item.link)) {
        matchedItem = item;
        break;
      }
    }

    // If no match found, default to Schedule (first item) instead of Employees
    if (!matchedItem) {
      if (url.includes('/employee')) {
        matchedItem = filteredItems.find((item) => item.label === 'Employees') || filteredItems[0];
      } else {
        // Default to Schedule (first item in the nav items array)
        matchedItem = filteredItems[0]; // Schedule is the first item
      }
    }

    if (this.activeItem() !== matchedItem) {
      this.activeItem.set(matchedItem);
      this.setIndicatorPosition();
      this.emitSubNavChanges(matchedItem, url);
    }
  }

  private emitSubNavChanges(item: any, currentUrl: string): void {
    if (item.subNav && item.subNav.length > 0) {
      this.subNavChange.emit(item.subNav);

      // Find the active subnav item based on current route
      // Find exact match first, then prefix match
      let activeSubNavItem = item.subNav.find(
        (subItem: SubNavItem) => currentUrl === subItem.link
      );

      // If no exact match, find prefix match
      if (!activeSubNavItem) {
        activeSubNavItem = item.subNav.find((subItem: SubNavItem) =>
          currentUrl.startsWith(subItem.link + '/')
        );
      }

      // Fallback to first item if no match
      activeSubNavItem = activeSubNavItem || item.subNav[0];

      this.activeSubNavChange.emit(activeSubNavItem);
    } else {
      this.subNavChange.emit([]);
      this.activeSubNavChange.emit(null);
    }
  }

  setActive(item: any): void {
    if (this.activeItem() !== item) {
      this.activeItem.set(item);
      this.setIndicatorPosition();
      this.emitSubNavChanges(item, this.router.url);
    } else {
      if (item.subNav && item.subNav.length > 0) {
        // Force change detection by emitting the first subnav item
        // This ensures ngOnChanges in subnav is always triggered
        this.subNavChange.emit(item.subNav);

        // Create a new reference to force change detection
        const firstSubNavItem = { ...item.subNav[0] };
        this.activeSubNavChange.emit(firstSubNavItem);
      }
    }
  }

  setIndicatorPosition(): void {
    setTimeout(() => {
      const activeElement = document.querySelector('.nav-item.active');
      if (activeElement) {
        const offsetLeft = (activeElement as HTMLElement).offsetLeft;
        const width = (activeElement as HTMLElement).offsetWidth;

        // Adjust position and width for edge items to create pill effect

        // Middle items: normal positioning
        this.indicatorPosition = `${offsetLeft}px`;
        this.indicatorWidth = `${width}px`;
      }
    }, 50);
  }


}
